import React, { useState, useEffect, useRef } from 'react';

/**
 * SvgCurve - A customizable SVG divider component that supports both static percentage
 * layouts and dynamic auto-adaptation based on the width of a reference text element.
 * 
 * @param {object} props
 * @param {React.RefObject} props.textRef - (Optional) React ref to the text element for auto-adaptation
 * @param {number} props.textPadding - Horizontal padding (px) after text ends before curve starts (default 24)
 * @param {number} props.curveWidth - Horizontal width (px) of the curve transition (default 120)
 * @param {number} props.width - Static virtual viewBox width (default 1200, ignored if textRef is used)
 * @param {number} props.height - Height of the SVG coordinate space (default 160)
 * @param {number} props.startY - Starting Y coordinate (left side height, default 0)
 * @param {number} props.endY - Ending Y coordinate (right side height, default 80)
 * @param {number} props.curveStartPercent - (Static) horizontal start of curve (default 25)
 * @param {number} props.curveEndPercent - (Static) horizontal end of curve (default 40)
 * @param {number} props.curveTension - Curve tension control, between 0 and 1 (default 0.5)
 * @param {string} props.fillColor - The fill color of the shape (default '#009ba1')
 * @param {string} props.className - Extra CSS classes to apply to the wrapper div
 */
export default function SvgCurve({
  textRef,
  textPadding = 24,
  curveWidth = 120,
  width = 1200,
  height = 160,
  startY = 0,
  endY = 80,
  curveStartPercent = 25,
  curveEndPercent = 40,
  curveTension = 0.5,
  fillColor = '#009ba1',
  className = '',
  ...props
}) {
  const containerRef = useRef(null);
  const [dimensions, setDimensions] = useState({ containerWidth: 1200, measuredTextWidth: 0 });

  // Handle dynamic measurement when textRef is provided
  useEffect(() => {
    if (!textRef) return;

    const updateDimensions = () => {
      if (!containerRef.current) return;
      
      const rect = containerRef.current.getBoundingClientRect();
      const containerWidth = rect.width || 1200;
      
      let measuredTextWidth = 0;
      if (textRef.current) {
        measuredTextWidth = textRef.current.getBoundingClientRect().width;
      }
      
      setDimensions({ containerWidth, measuredTextWidth });
    };

    // Initial measurement
    updateDimensions();

    // Use ResizeObserver for highly responsive measurements on resize / text change (guarded for older Safari/WebViews)
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateDimensions();
      });
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
      if (textRef.current) {
        resizeObserver.observe(textRef.current);
      }
    }

    // Fallback: observe window resize as well (works on all browsers)
    window.addEventListener('resize', updateDimensions);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', updateDimensions);
    };
  }, [textRef]);

  // Determine final coordinates based on mode
  let finalWidth = width;
  let curveStartX = 0;
  let curveEndX = 0;

  if (textRef) {
    // Dynamic Mode
    finalWidth = dimensions.containerWidth;
    
    // curveStartX is the text width + padding, constrained so the curve fits
    curveStartX = Math.min(
      dimensions.measuredTextWidth + textPadding,
      finalWidth - curveWidth
    );
    curveEndX = Math.min(curveStartX + curveWidth, finalWidth);
  } else {
    // Static Mode
    curveStartX = (width * curveStartPercent) / 100;
    curveEndX = (width * curveEndPercent) / 100;
  }

  const dx = curveEndX - curveStartX;

  // Control points for cubic Bezier curve (creates a smooth, horizontal transition)
  const cp1x = curveStartX + dx * curveTension;
  const cp1y = startY;
  const cp2x = curveEndX - dx * curveTension;
  const cp2y = endY;

  // Construct SVG path data
  const pathData = [
    `M 0,${height}`,                         // 1. Move to bottom-left
    `L 0,${startY}`,                         // 2. Line up to left top height
    `L ${curveStartX},${startY}`,             // 3. Flat horizontal line to curve start
    `C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curveEndX},${endY}`, // 4. Cubic Bezier curve transition
    `L ${finalWidth},${endY}`,               // 5. Flat horizontal line to right edge
    `L ${finalWidth},${height}`,             // 6. Line down to bottom-right
    'Z'                                      // 7. Close path
  ].join(' ');

  return (
    <div 
      ref={containerRef} 
      className={className} 
      style={{ width: '100%', position: 'relative', overflow: 'hidden' }}
      {...props}
    >
      <svg
        viewBox={`0 0 ${finalWidth} ${height}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block' }}
      >
        <path d={pathData} fill={fillColor} />
      </svg>
    </div>
  );
}
